import { useState, useMemo } from "react";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import { VisualizeIcon, VisualizeOffIcon } from "../../../shared/icons";
import { changePasswordService } from "../../services";
import { validatePassword } from "../../../shared/validatePassword";
import { newValidatePassword } from "../../../shared/newValidatePassword";
import { newParticipantStyles } from "./changePasswordPage.styles";
import CustomTooltip from "../../../shared/components/customTooltip/customTooltip";

const initialFormState = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
  showPassword: false,
};

export const ChangePasswordPage = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formValues, setFormValues] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState(initialFormState);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [newPasswordErrors, setNewPasswordErrors] = useState({
    errorList: newValidatePassword("", "").errors,
    isValid: false,
  });

  const classes = newParticipantStyles();

  const getIsDisabled = () => {
    return (
      isUpdating ||
      formErrors.newPassword !== "" ||
      formErrors.confirmPassword !== "" ||
      formValues.newPassword === "" ||
      formValues.confirmPassword === "" ||
      formValues.currentPassword === ""
    );
  };

  const handleBlur = (e) => {
    const { value, name } = e.target;

    if (
      formValues.newPassword === "" &&
      formValues.confirmPassword === "" &&
      formValues.currentPassword === ""
    ) {
      setFormErrors(initialFormState);
      return;
    }

    if (!value) {
      setFormErrors({ ...formErrors, [name]: "This field is required" });
      return;
    }

    const validationResult = validatePassword(value);
    if (!validationResult.isValid) {
      setFormErrors({
        ...formErrors,
        [name]:
          "Must include 8 characters, 1 number, 1 special character and 1 alphabetic value at least",
      });
      return;
    }

    if (name === "confirmPassword" && value !== formValues.newPassword) {
      setFormErrors({
        ...formErrors,
        [name]: "The password doesn't match",
      });
      return;
    }

    setFormErrors({
      ...formErrors,
      [name]: "",
    });
  };

  const confimPasswordMatch = (name, value) => {
    let result;

    if (name === "confirmPassword" || name === "newPassword")
      result = {
        confirmPassword:
          value !== formValues.confirmPassword &&
          value !== formValues.newPassword
            ? "The password doesn't match"
            : "",
      };

    setFormErrors({
      ...formErrors,
      ...result,
    });
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormValues({ ...formValues, [name]: value });
    confimPasswordMatch(name, value);

    if (name === "newPassword") {
      const newValidationResult = newValidatePassword(value, "");
      setNewPasswordErrors({
        ...newPasswordErrors,
        errorList: newValidationResult.errors,
        isValid: newValidationResult.isValid,
      });
    }
  };

  const tooltipMemo = useMemo(() => {
    return tooltipOpen && !newPasswordErrors.isValid ? (
      <CustomTooltip>
        <b>Password Must Include:</b>
        <ul className={classes.errorList}>
          {newPasswordErrors.errorList.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </CustomTooltip>
    ) : null;
  }, [
    tooltipOpen,
    newPasswordErrors.isValid,
    newPasswordErrors.errorList,
    classes.errorList,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    const response = await changePasswordService({
      currentPassword: formValues.currentPassword,
      password: formValues.newPassword,
      confirmPassword: formValues.confirmPassword,
    });

    setIsUpdating(false);

    if (response.status !== 200) {
      const message = response.data.errors.length
        ? response.data.errors.toString()
        : response.data.warnings.toString();
      if (formValues.newPassword === "") {
        setErrorMessage("Password field is required");
      } else if (formValues.confirmPassword === "") {
        setErrorMessage("Confirm password field is required");
      } else if (formValues.currentPassword === "") {
        setErrorMessage("Current password field is required");
      } else {
        setErrorMessage(message.replace("Username/", ""));
      }
      return;
    }
    setErrorMessage("Password has correctly changed");
  };

  const handleClickShowPassword = () => {
    setFormValues({ ...formValues, showPassword: !formValues.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Container component="main" maxWidth="sm" className={classes.container}>
      <Grid
        container
        spacing={4}
        direction="column"
        className={classes.gridContainer}
      >
        <Box mt={0} />
        <Grid>
          {errorMessage && (
            <Typography align="center" className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          )}
        </Grid>

        <Grid>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={0} direction="column">
              <Grid item>
                <TextField
                  variant="outlined"
                  margin="normal"
                  type="password"
                  name="currentPassword"
                  label="Current Password"
                  id="currentPassword"
                  onChange={handleChange}
                  value={formValues.currentPassword}
                  error={!!formErrors.currentPassword.length}
                  className={classes.customTextfield}
                />
              </Grid>
              <Grid item className={classes.inputBox}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  type={formValues.showPassword ? "text" : "password"}
                  name="newPassword"
                  label="New Password"
                  id="newPassword"
                  fullWidth
                  onBlur={(e) => {
                    handleBlur(e);
                    setTooltipOpen(false);
                  }}
                  onChange={handleChange}
                  value={formValues.newPassword}
                  error={!!formErrors.newPassword.length}
                  className={classes.customTextfield}
                  onFocus={() => setTooltipOpen(true)}
                  InputProps={{
                    endAdornment: (
                      <>
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            className={classes.endAdornment}
                          >
                            {formValues.showPassword ? (
                              <VisualizeIcon
                                className={classes.showPasswordIcon}
                              />
                            ) : (
                              <VisualizeOffIcon
                                className={classes.showPasswordIcon}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                        <InputAdornment position="end">
                          {newPasswordErrors.isValid && (
                            <CheckCircleIcon
                              className={classes.passwordAcceptedIcon}
                            />
                          )}
                        </InputAdornment>
                      </>
                    ),
                  }}
                />
                {tooltipMemo}
              </Grid>

              <Grid item>
                <TextField
                  variant="outlined"
                  margin="normal"
                  type="password"
                  name="confirmPassword"
                  label="Confirm Password"
                  id="confirmPassword"
                  fullWidth
                  onChange={handleChange}
                  value={formValues.confirmPassword}
                  error={!!formErrors.confirmPassword.length}
                  className={classes.customTextfield}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formValues.confirmPassword !== "" &&
                          formErrors.confirmPassword === "" && (
                            <CheckCircleIcon
                              className={classes.passwordAcceptedIcon}
                            />
                          )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={getIsDisabled()}
                  className={classes.changePsswdBtn}
                >
                  Change password
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Container>
  );
};
